import { message } from "antd";
import { JointContent } from "antd/es/message/interface";
import {
  AppInterface,
  AppRefsInterface,
  AppStateInterface,
  ServicesInterface,
} from "..";
import { Store } from "../classes/Store";
import { CommandBarService } from "./CommandBarService";
import { EventBusService } from "./EventBusService";
import { NavigationService } from "./NavigationService";
import { RouterService } from "./RouterService";
import { ServiceWorkerService } from "./ServiceWorkerService";
import { SourceService } from "./SourceService";
import { StorageService } from "./StorageService";
import { UserInterfaceService } from "./UserInterfaceService";
import { UserService } from "./UserService";
import { UserService2 } from "./UserService2";

message.config({
  top: 50,
});

class AntdMessage {
  success(
    content: JointContent,
    duration?: number | VoidFunction,
    onClose?: VoidFunction
  ) {
    return message.success(content, duration, onClose);
  }
  info(
    content: JointContent,
    duration?: number | VoidFunction,
    onClose?: VoidFunction
  ) {
    return message.info(content, duration, onClose);
  }
  warning(
    content: JointContent,
    duration?: number | VoidFunction,
    onClose?: VoidFunction
  ) {
    return message.warning(content, duration, onClose);
  }
  error(
    content: JointContent,
    duration?: number | VoidFunction,
    onClose?: VoidFunction
  ) {
    return message.error(content, duration, onClose);
  }
}

export class FrameworkService implements AppInterface {
  id: AppInterface["id"] = "opendash";
  config: AppInterface["config"] = undefined;
  ui: AppInterface["ui"];
  state: Store<AppStateInterface>;

  services: ServicesInterface;

  _internal: AppInterface["_internal"] = {
    globalComponents: [],
    providerComponents: [],
    services: [],
    routes: [],
  };

  _refs: AppRefsInterface = {
    content: { current: null },
    layout: { current: null },
    headerBeforeMenuLeft: { current: null },
    headerAfterMenuLeft: { current: null },
    headerBeforeMenuRight: { current: null },
    headerAfterMenuRight: { current: null },
    headerBeforeLogo: { current: null },
    headerAfterLogo: { current: null },
    footer: { current: null },
    footerAfterCopyright: { current: null },
  };

  user = new UserService2(this);
  router = new RouterService(this);
  ui2 = new UserInterfaceService(this);
  serviceWorker = new ServiceWorkerService(this);
  commands = new CommandBarService(this);
  antd_msg = new AntdMessage();

  constructor() {
    // @ts-ignore
    window.$framework = this;

    // debugger;
    try {
      this.state = createState();
    } catch (error) {
      console.error(error);
    }

    this.ui = {
      disableHeader: false,
      disableHeaderSourcePicker: false,
      disableNotificationHeaderSettings: false,
      disableChangePasswordHeaderSettings: false,
      disableFooter: false,
      logoImage: undefined,
      logoText: undefined,
      logoLink: undefined,
      logoLinkExternal: undefined,
      logoMobileSize: 275,

      primaryColor: "",
      backgroundColor: "",
      colorPaletteHex: [],
      colorPaletteRGB: [],
      languages: [],
      staticNavigationGroups: [],
      staticNavigationItems: [],
    };

    this.services = {
      UserService: new UserService(this),
      SourceService: new SourceService(this),
      NavigationService: new NavigationService(this),
      DeviceStorageService: new StorageService(this),
      UserStorageService: new StorageService(this),
      EventBusService: EventBusService.getInstance(),
    };
  }

  init(data: Partial<AppInterface>) {
    if (data.ui) {
      Object.assign(this.ui, data.ui);
    }

    if (data._internal) {
      Object.assign(this._internal, data._internal);
    }

    delete data.ui;
    delete data.ui2;
    delete data.services;
    delete data._internal;

    Object.assign(this, data);

    this.setServicesLoading();

    for (const service of this._internal.services) {
      service.subscribe(() => {
        this.setServicesLoading();
      });
    }

    this.commands.registerCommand({
      id: "opendash:navigation",
      title: "Navigation",
    });

    this.commands.registerCommand({
      id: "opendash:navigation:home",
      parent: "opendash:navigation",
      title: "Home",
      shortcut: ["g", "h"],
      handler: () => {
        this.router.navigate("/");
      },
    });

    for (const nav of this.ui.staticNavigationItems) {
      if (nav.place !== "frontpage") continue;
      if (typeof nav.link !== "string") continue;

      this.commands.registerCommand({
        id: "opendash:navigation:" + nav.id,
        parent: "opendash:navigation",
        title: nav.label,
        handler: () => {
          this.router.navigate(nav.link as string);
        },
      });
    }

    this.commands.registerCommand({
      id: "opendash:debug",
      title: "Debug",
    });

    this.commands.registerCommand({
      id: "opendash:debug:translations",
      parent: "opendash:debug",
      title: "Debug Translations",
      handler: () => {
        this.router.navigate("/debug/translations");
      },
    });
    this.commands.registerCommand({
      id: "opendash:debug:icons",
      parent: "opendash:debug",
      title: "Debug Icons",
      handler: () => {
        this.router.navigate("/debug/icons");
      },
    });
  }

  private isServicesLoading() {
    if (!this.services.UserService.isLoggedIn()) {
      return this.services.UserService.isLoading();
    }

    for (const service of this._internal.services) {
      if (service.isLoading()) {
        return true;
      }
    }

    return false;
  }

  private setServicesLoading() {
    const loading = this.isServicesLoading();

    this.ui2.setServicesLoading(loading);
  }
}

function createState(): Store<AppStateInterface> {
  const cacheKey = "opendash/state/cache";
  const cache = loadCache(cacheKey);

  const store = new Store<AppStateInterface>(
    cache || {
      sources: {
        current: undefined,
        all: [],
      },
      navigation: {
        userNavigationGroups: [],
        userNavigationItems: [],
      },
    }
  );

  store.subscribe(() => {
    saveCache(cacheKey, store.getState());
  });

  return store;
}

function loadCache(cacheKey: string): AppStateInterface | null {
  try {
    return JSON.parse(window.localStorage.getItem(cacheKey));
  } catch (error) {
    return null;
  }
}

function saveCache(cacheKey: string, state: AppStateInterface) {
  try {
    window.localStorage.setItem(cacheKey, JSON.stringify(state));
  } catch (error) {
    console.error(`Error writing ${cacheKey}`, error);
  }
}
